import {useEffect, useState} from 'react';
import {useApplication} from '../contexts';

/**
 * Returns the latest audio end point in seconds across all audio clips,
 * or null if there are no audio clips.
 *
 * Reacts to clip list changes and audio data loading.
 */
export function useAudioEndTime(): number | null {
  const {player} = useApplication();
  const [endTime, setEndTime] = useState<number | null>(null);

  useEffect(() => {
    const unsubs: (() => void)[] = [];

    const recalc = () => {
      const clips = player.onAudioClipsChanged.current;
      if (clips.length === 0) {
        setEndTime(null);
        return;
      }

      let max = 0;
      for (const clip of clips) {
        const dur = player.audioResources.peekDuration(clip.src);
        const end = clip.offset + dur;
        if (end > max) max = end;
      }
      setEndTime(max > 0 ? max : null);
    };

    // Recalculate when clip list changes
    unsubs.push(player.onAudioClipsChanged.subscribe(() => {
      // Clean up old audio data subscriptions and re-subscribe
      while (unsubs.length > 1) unsubs.pop()?.();

      const clips = player.onAudioClipsChanged.current;
      for (const clip of clips) {
        const resource = player.audioResources.get(clip.src);
        unsubs.push(resource.onData.subscribe(recalc));
      }

      recalc();
    }));

    // Initial subscription for existing clips
    const clips = player.onAudioClipsChanged.current;
    for (const clip of clips) {
      const resource = player.audioResources.get(clip.src);
      unsubs.push(resource.onData.subscribe(recalc));
    }

    recalc();

    return () => unsubs.forEach(u => u());
  }, [player]);

  return endTime;
}
